import React from 'react';
import { signIn } from 'next-auth/react';
import { FieldValues, useForm } from 'react-hook-form';
import { authService } from '@/api';
import {
	Button,
	ErrorMessageInput,
	Spinner,
	Typography,
} from '@/components/common';
import { InputEmail } from '@/components/form';
import AppRoutes from '@/const/routes';
import { useNotification, useUser } from '@/hooks';
import { setLocalStorage } from '@/utils/localStorage';
import { useRouter } from 'next/router';
import { formatErrorMessage } from '@/lib';
import { ErrorHttpResponse } from '@/interfaces';
import { ResetCard } from '@/components/auth/ResetCard';
import { AuthLayout } from '@/components/layout/layout/AuthLayout';

export const ReactivateAccountScreen = () => {
	const router = useRouter();
	const { id, type } = router.query;
	const { user } = useUser();
	const { toastSuccess, toastError } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [errorMessage, setErrorMessage] = React.useState<string>('');
	const [email, setEmail] = React.useState<string>('');
	const [isLoadingScreen, setIsLoadingScreen] = React.useState(true);
	const [recoveredSocial, setRecoveredSocial] = React.useState<boolean | null>(
		null
	);

	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
		},
	};
	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isDirty, isValid },
	} = useForm();

	React.useEffect(() => {
		if (!router.isReady) return;

		const fetchUserStatus = async () => {
			try {
				const res = await authService.getUserActiveStatus(id as string);
				if (res.data.activated) {
					return user
						? router.push(
								`${AppRoutes.CLIENTAPP_DASHBOARD}?showUserActiveModal=true`
						  )
						: router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
				} else {
					if (type === 'social') {
						handleReactivateAccount(id as string);
					} else if (type === 'credentials') {
						onSuccess();
						await new Promise((resolve) => setTimeout(resolve, 3000));
						router.push({
							pathname: AppRoutes.CLIENTAPP_AUTH_REACTIVATE_ACCOUNT_REQUEST,
							query: router.query,
						});
					} else {
						setRecoveredSocial(false);
						setIsLoadingScreen(false);
					}
				}
			} catch (error) {
				console.log(error);
			}
		};
		if (id) {
			fetchUserStatus();
		} else {
			setIsLoadingScreen(false);
			setRecoveredSocial(false);
		}
	}, [id, type, router, user]);

	const handleReactivateAccount = async (id: string): Promise<void> => {
		try {
			const response = await authService.recoverSocialAccountById(id as string);
			onSuccess();
			await new Promise((resolve) => setTimeout(resolve, 3000));
			if (type === 'social') {
				loginAccount(response);
			}
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const onSuccess = () => {
		setRecoveredSocial(true);
		setIsLoadingScreen(false);
	};

	const loginAccount = async (data: any) => {
		try {
			await signIn('recovery-token', {
				user: JSON.stringify(data),
				callbackUrl: `${AppRoutes.CLIENTAPP_DASHBOARD}?confirm-account=reactivated`,
			});
		} catch (error) {
			const status = (error as ErrorHttpResponse).statusCode;
			if (status !== 409) {
				toastError(formatErrorMessage((error as any).message));
			}
		}
	};

	const handleResendEmail = async (): Promise<void> => {
		try {
			const response = await authService.resendRecoverAccountEmail(email);
			toastSuccess(response.data.message);
		} catch (error: any) {
			toastError(error.message);
		}
	};

	const onSubmit = async (values: FieldValues): Promise<void> => {
		setIsLoading(true);
		try {
			await authService.recoverAccount(values.email);
			setLocalStorage('rca', values.email);
			setTimeout(() => {
				setEmail(values.email);
			}, 1000);
		} catch (error: any) {
			toastError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const getTitle = (): string => {
		if (isLoadingScreen) return '';
		if (email) return 'Check your email';
		if (recoveredSocial) return 'Your Account Has Been Successfully Activated';

		return 'Reactivate your Account';
	};

	const getImage = () => {
		if (isLoadingScreen) return undefined;
		if (recoveredSocial) return '/img/landing/svg/black-magic-woman-alt.svg';
		if (email) return '/img/landing/svg/white-boy-alt.svg';

		return undefined;
	};

	const handleBack = () => {
		router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN, undefined, { shallow: true });
	};

	return (
		<AuthLayout withCarrousel={false}>
			<ResetCard
				title={getTitle()}
				srcImage={getImage()}
				classNameTitle="!text-4xl lg:px-6"
				handleBack={handleBack}
			>
				<>
					{isLoadingScreen ? (
						<Spinner type="loadingPage" className="m-auto mt-10" />
					) : (
						<>
							{email && (
								<div className="">
									<div>
										<Typography type="regular-18" className="text-center">
											We&apos;ve sent an email to reactivate your account to{' '}
											<br /> {`${email}`} <br />
											Don&apos;t forget to check your Spam folder!
										</Typography>
									</div>
									<div className="flex justify-center items-center gap-1 !mt-[34px]">
										<Typography type="regular-16">
											{`Didn&apos;t get the email?`}
										</Typography>
										<button onClick={handleResendEmail}>
											<Typography type="semibold-16" className="text-primary">
												Resend
											</Typography>
										</button>
									</div>
								</div>
							)}
							{!email && (
								<>
									{recoveredSocial && (
										<>
											<Spinner
												type="loadingPage"
												className="w-12 h-12 my-4 mx-auto"
											/>
											<Typography
												type="regular-16"
												className="text-gray-35 text-center"
											>
												Redirecting you to the Claim Magic application in a few
												seconds
											</Typography>
										</>
									)}
									{recoveredSocial === false && (
										<form
											className="space-y-8"
											onSubmit={handleSubmit(onSubmit)}
										>
											<div>
												<Typography type="regular-18" className="text-center">
													To reactivate your account, please enter the email
													address you <br /> originally signed up with, and we
													will send instructions to that <br /> email address to
													complete the reactivation process.
												</Typography>
											</div>
											<div className="mb-10 mt-4 space-y-8">
												<InputEmail
													name="email"
													title="Email"
													register={register}
													rules={rules.email}
													error={errors.email}
													onChangeCustom={() => setErrorMessage('')}
													setValueCustom={setValue}
												/>
												{errorMessage && (
													<ErrorMessageInput message={errorMessage} />
												)}
												<Button
													type="submit"
													size="fit"
													decoration="primary"
													disabled={isLoading || !isDirty || !isValid}
													label="Reactivate Account"
													className="!w-[300px] !h-16 mx-auto"
												/>
											</div>
										</form>
									)}
								</>
							)}
						</>
					)}
				</>
			</ResetCard>
		</AuthLayout>
	);
};
